import {
  modals,
} from '@mantine/modals';
import {
  notifications,
} from '@mantine/notifications';

import NewReleasesIcon from '@mui/icons-material/NewReleases';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListSubheader,
  ThemeProvider,
} from '@mui/material';
import {
  createFileRoute,
  useNavigate,
} from '@tanstack/react-router';
import {
  useQueryClient,
} from '@tanstack/react-query';
import Papa from 'papaparse';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Offcanvas,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import {
  useAuth,
} from 'react-oidc-context';

import {
  darkTheme,
} from '#components/themes';
import {
  TransparentButton,
} from '#components/ui';
import SyncClientsModal from '#features/gary/SyncClientsModal';
import {
  formatAbn,
  validateAbn,
} from '@specia-accountant/shared';
import {
  EditClient,
} from '#features/client/EditClient';
import {
resolveObject,
} from '@specia-accountant/shared';
import MessageModalModified from '#utils/components/MessageModalModified';

interface LoaderDataResult {
  clients: ClientWithId[];
  users: any;
  availableAccounts: any;
  subscription: any;
}
export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/gary/_garyLayout/clients',
)({
  beforeLoad: () => {
    return {
      crumb: 'Clients',
    };
  },
  loader: async (opts): Promise<LoaderDataResult> => {
    const clients: Promise<any> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'creator',
        'clients',
      ],
    });
    const users: Promise<any> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'users',
      ],
    });
    const availableAccounts: Promise<any> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'creator',
        'as-accounts',
      ],
    });
    const subscription: Promise<any> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'subscription',
        'maxclient',
      ],
    });
    const result = await resolveObject({
      clients,
      users,
      availableAccounts,
      subscription,
    });
    return result;
  },
  component: RouteComponent,
});

function AddClientsModal(
  clientAdding: string,
  currentABN: string,
) {
  return (
    <>
      <h4
        style={ {
          textAlign: 'center',
          fontSize: '1.3vw',
          marginBottom: '3vw',
          fontWeight: '500',
        } }
      >
        { clientAdding === 'init' && <>New Client</> }
        { clientAdding === 'details' && <>{ currentClientName }</> }
        { clientAdding === 'added' && <>Client Successfully Added</> }
        { clientAdding === 'error' && <>{ errorTitle }</> }
      </h4>
      { clientAdding === 'init' && (
        <>
          <Form>
            { /* Client Name */ }
            <Form.Group
              style={ {
                marginBottom: '2vw',
                textAlign: 'left',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                  marginLeft: '0.2vw',
                } }
              >
                Client Name
              </p>
              <p
                style={ {
                  fontSize: '0.75vw',
                  color: 'rgb(255, 255, 255, 0.6)',
                  marginLeft: '0.2vw',
                } }
              >
                Client name must be unique across your organization.
              </p>
              <Form.Control
                style={ {
                  height: '3vw',
                  backgroundColor: 'rgba(0,0,0,.25)',
                  border: 'none',
                  borderRadius: '0.5vw',
                  fontSize: '0.8vw',
                  paddingLeft: '1vw',
                } }
                type="text"
                required
                placeholder="Client Name"
                value={ currentClientName }
                ref={ clientNameRef }
                onChange={ e => setCurrentClientName(e.target.value) }
              />
            </Form.Group>
            { /* Accounting Software */ }
            <Form.Group
              style={ {
                marginBottom: '2vw',
                textAlign: 'left',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                  marginLeft: '0.2vw',
                } }
              >
                ATO Data Retrieval
              </p>
              <p
                style={ {
                  fontSize: '0.75vw',
                  color: 'rgb(255, 255, 255, 0.6)',
                  marginLeft: '0.2vw',
                } }
              >
                This option allows Gary to download data from Australian Tax Office and populate corresponding tabs in workpaper.
              </p>
              <div
                style={ {
                  display: 'inline-block',
                  backgroundColor: 'rgb(0, 0, 0, 0.2)',
                  borderRadius: '0.6vw',
                  padding: '0.3vw',
                } }
              >
                { [
                  true,
                  false,
                ].map((option, index) => (
                  <span
                    style={ {} }
                  >
                    <TransparentButton
                      style={ {
                        padding: '0.3vw 0.75vw',
                        borderRadius: '0.5vw',
                      } }
                      onClick={ () => {
                        setCurrentATORetrieveal(option);
                      } }
                      selected={ currentATORetrieveal === option }
                    >
                      { option ? 'Yes' : 'No' }
                    </TransparentButton>
                  </span>
                )) }
              </div>
            </Form.Group>
            { /* Accounting Software */ }
            <Form.Group
              style={ {
                marginBottom: '2vw',
                textAlign: 'left',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                  marginLeft: '0.2vw',
                } }
              >
                Accounting Software
              </p>
              <p
                style={ {
                  fontSize: '0.75vw',
                  color: 'rgb(255, 255, 255, 0.6)',
                  marginLeft: '0.2vw',
                } }
              >
                This option allows Gary to download reports from the chosen accounting software and populate corresponding tabs in workpaper.
              </p>
              <div
                style={ {
                  display: 'inline-block',
                  backgroundColor: 'rgb(0, 0, 0, 0.2)',
                  borderRadius: '0.6vw',
                  padding: '0.3vw',
                } }
              >
                { [
                  'XERO',
                  'MYOB',
                  'NONE',
                ].map((option, index) => (
                  <span
                    style={ {} }
                  >
                    <TransparentButton
                      style={ {
                        padding: '0.3vw 0.75vw',
                        borderRadius: '0.5vw',
                      } }
                      onClick={ () => {
                        setCurrentAccountingSoftware(option.toLowerCase());
                      } }
                      selected={ currentAccountingSoftware === option.toLowerCase() }
                    >
                      { option }
                    </TransparentButton>
                  </span>
                )) }
              </div>
            </Form.Group>
          </Form>
        </>
      ) }
      { clientAdding === 'details' && (
        <>
          <Form>
            { /* ABN */ }
            <Form.Group
              style={ {
                marginBottom: '2vw',
                textAlign: 'left',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                  marginLeft: '0.2vw',
                } }
              >
                Australian Business Number
                { !currentATORetrieveal && (
                  <span
                    style={ {
                      color: 'rgb(255, 255, 255, 0.6)',
                      fontSize: '0.75vw',
                    } }
                  >
                    (Optional)
                  </span>
                ) }
              </p>
              <p
                style={ {
                  fontSize: '0.75vw',
                  color: 'rgb(255, 255, 255, 0.6)',
                  marginLeft: '0.2vw',
                } }
              >
                Australian Business Number associated with the provided client name.
              </p>

              <Form.Control
                style={ {
                  height: '3vw',
                  backgroundColor: 'rgba(0,0,0,.25)',
                  border: 'none',
                  borderRadius: '0.5vw',
                  fontSize: '0.8vw',
                  paddingLeft: '1vw',
                } }
                value={ currentABN }
                type="text"
                placeholder="Client ABN"
                ref={ abnRef }
                onChange={ (e) => {
                  setCurrentABN(e.target.value);
                  setAbnValidity(validateAbn(e.target.value));
                } }
              />
              { !abnValidity && currentATORetrieveal && (
                <p
                  style={ {
                    color: 'red',
                    fontSize: '0.8vw',
                    marginTop: '0.5vw',
                  } }
                >
                  Invalid ABN
                </p>
              ) }
            </Form.Group>
            { /* Alternative Name */ }
            <Form.Group
              style={ {
                marginBottom: '2vw',
                textAlign: 'left',
                display: 'none',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                } }
              >
                Alternative Name (optional)
              </p>
              <Form.Control
                style={ {
                  height: '2.5vw',
                  backgroundColor: 'rgba(255,255,255,.075)',
                  border: 'none',
                  borderRadius: '0.5vw',
                  fontSize: '0.8vw',
                  paddingLeft: '1vw',
                } }
                type="text"
                placeholder="Enter alternative name"
                ref={ alternativeNameRef }
              />
            </Form.Group>
            { /* AS Client Name */ }
            { currentAccountingSoftware !== 'none' && (
              <Form.Group
                style={ {
                  marginBottom: '2vw',
                  textAlign: 'left',
                } }
              >
                <p
                  style={ {
                    fontSize: '0.9vw',
                    fontWeight: '500',
                    marginLeft: '0.2vw',
                  } }
                >
                  Accounting Software Client Name
                </p>
                <p
                  style={ {
                    fontSize: '0.75vw',
                    color: 'rgb(255, 255, 255, 0.6)',
                    marginLeft: '0.2vw',
                  } }
                >
                  Please provide the same Client Name as displayed in the chosen accounting software.
                </p>
                <Form.Control
                  style={ {
                    height: '3vw',
                    backgroundColor: 'rgba(0,0,0,.25)',
                    border: 'none',
                    borderRadius: '0.5vw',
                    fontSize: '0.8vw',
                    paddingLeft: '1vw',
                  } }
                  type="text"
                  placeholder="AS Client Name"
                  value={ currentASClientName }
                  ref={ asClientNameRef }
                  onChange={ e => setCurrentASClientName(e.target.value) }
                />
              </Form.Group>
            ) }
            { /* Password (Optional) */ }
            { false && (
              <Form.Group
                style={ {
                  marginBottom: '2vw',
                  textAlign: 'left',
                } }
              >
                <p
                  style={ {
                    fontSize: '0.9vw',
                    fontWeight: '500',
                  } }
                >
                  MYOB Client Password (Optional)
                </p>
                <Form.Control
                  style={ {
                    height: '2.5vw',
                    backgroundColor: 'rgba(255,255,255,.1)',
                    border: '1px solid rgb(255, 255, 255, 0.2)',
                    borderRadius: '0.4vw',
                    fontSize: '1vw',
                    paddingLeft: '1vw',
                  } }
                  type="password"
                  placeholder="Client Password"
                  ref={ passwordOptionalRef }
                />
              </Form.Group>
            ) }
            { /* Accounting Basis */ }
            <Form.Group
              style={ {
                display: 'none',
                marginBottom: '2vw',
                textAlign: 'left',
              } }
            >
              <p
                style={ {
                  fontSize: '0.9vw',
                  fontWeight: '500',
                } }
              >
                Accounting Basis
              </p>
              <p
                style={ {
                  fontSize: '0.75vw',
                  color: 'rgb(255, 255, 255, 0.6)',
                } }
              >
                Please provide the accounting basis for this client.
              </p>
              <div
                style={ {
                  display: 'inline-block',
                  backgroundColor: 'rgb(255, 255, 255, 0.05)',
                  borderRadius: '2vw',
                  padding: '0.15vw',
                } }
              >
                <span
                  style={ {} }
                >
                  <TransparentButton
                    style={ {
                      padding: '0.3vw 0.75vw',
                    } }
                    onClick={ () => {
                      setcurrentAccountingBasis('Cash');
                    } }
                    selected={ currentAccountingBasis === 'Cash' }
                  >
                    Cash
                  </TransparentButton>
                </span>
                <span
                  style={ {} }
                >
                  <TransparentButton
                    style={ {
                      padding: '0.3vw 0.75vw',
                    } }
                    onClick={ () => {
                      setcurrentAccountingBasis('Accrual');
                    } }
                    selected={ currentAccountingBasis === 'Accrual' }
                  >
                    Accrual
                  </TransparentButton>
                </span>
              </div>
            </Form.Group>
            { /* BAS Lodgement */ }
            { currentATORetrieveal && (
              <>
                <Form.Group
                  style={ {
                    display: 'none',
                    marginBottom: '2vw',
                    textAlign: 'left',
                  } }
                >
                  <p
                    style={ {
                      fontSize: '0.9vw',
                      fontWeight: '500',
                    } }
                  >
                    BAS Lodgement
                  </p>
                  <p
                    style={ {
                      fontSize: '0.75vw',
                      color: 'rgb(255, 255, 255, 0.6)',
                    } }
                  >
                    Please provide the frequency of BAS Lodgment.
                  </p>

                  <div
                    style={ {
                      display: 'inline-block',
                      backgroundColor: 'rgb(255, 255, 255, 0.05)',
                      borderRadius: '2vw',
                      padding: '0.15vw',
                    } }
                  >

                    <span
                      style={ {} }
                    >

                      <TransparentButton
                        style={ {
                          padding: '0.3vw 0.75vw',
                        } }
                        onClick={ () => {
                          setcurrentBasLodgement('Quarterly');
                        } }
                        selected={ currentBasLodgement === 'Quarterly' }
                      >
                        Quarterly
                      </TransparentButton>

                    </span>

                    <span
                      style={ {} }
                    >
                      <TransparentButton
                        style={ {
                          padding: '0.3vw 0.75vw',
                        } }
                        onClick={ () => {
                          setcurrentBasLodgement('Monthly');
                        } }
                        selected={ currentBasLodgement === 'Monthly' }
                      >
                        Monthly
                      </TransparentButton>
                    </span>

                  </div>

                </Form.Group>
              </>
            ) }
          </Form>
        </>
      ) }
      { clientAdding === 'processing' && (
        <>
          <Form.Group
            controlId="formFileLg"
            className="mb-3"
          >
            <div
              style={ {
                textAlign: 'center',
                marginTop: '25vh',
              } }
            >
              <Spinner
                animation="border"
                variant="secondary"
                style={ {
                  width: '5rem',
                  height: '5rem',
                } }
              />
              <p
                style={ {
                  fontSize: '1.1rem',
                  fontWeight: '400',
                  marginTop: '30px',
                } }
              >
                Adding client to the queue
              </p>
            </div>
          </Form.Group>
        </>
      ) }
      { clientAdding === 'added' && (
        <>
          <div
            style={ {
              textAlign: 'left',
            } }
          >
            { formatAbn(currentABN) && (
              <>
                <p
                  style={ {
                    fontWeight: '600',
                    fontSize: '0.9vw',
                  } }
                >
                  Client ABN
                </p>
                <p
                  style={ {
                    color: 'rgb(255, 255, 255, 0.7)',
                    fontSize: '0.8vw',
                  } }
                >
                  <span
                    style={ {
                      fontFamily: '\'Consolas\', Courier, monospace',
                    } }
                  >
                    { formatAbn(currentABN) || 'NOT PROVIDED' }
                  </span>
                </p>
              </>
            ) }
            <p
              style={ {
                fontWeight: '600',
                fontSize: '0.9vw',
                paddingTop: '1vw',
              } }
            >
              Client Name
            </p>
            <p
              style={ {
                color: 'rgb(255, 255, 255, 0.7)',
                fontSize: '0.8vw',
              } }
            >
              { currentClientName }
            </p>
            <p
              style={ {
                fontWeight: '600',
                fontSize: '0.9vw',
                paddingTop: '1vw',
              } }
            >
              ATO Data Retrieval
            </p>
            <p
              style={ {
                color: 'rgb(255, 255, 255, 0.7)',
                fontSize: '0.8vw',
              } }
            >
              { currentATORetrieveal ? 'YES' : 'NO' }
            </p>
            <p
              style={ {
                fontWeight: '600',
                fontSize: '0.9vw',
                paddingTop: '1vw',
              } }
            >
              Accounting Software
            </p>
            <p
              style={ {
                color: 'rgb(255, 255, 255, 0.7)',
                fontSize: '0.8vw',
              } }
            >
              { currentAccountingSoftware.toUpperCase() }
            </p>
            { currentAccountingSoftware !== 'none' && (
              <>
                <p
                  style={ {
                    fontWeight: '600',
                    fontSize: '0.9vw',
                    paddingTop: '1vw',
                  } }
                >
                  AS Client Name
                </p>
                <p
                  style={ {
                    color: 'rgb(255, 255, 255, 0.7)',
                    fontSize: '0.8vw',
                  } }
                >
                  { currentASClientName }
                </p>
              </>
            ) }
            { currentATORetrieveal && (
              <>
                <p
                  style={ {
                    fontWeight: '600',
                    fontSize: '0.9vw',
                    paddingTop: '1vw',
                  } }
                >
                  Accounting Basis
                </p>
                <p
                  style={ {
                    color: 'rgb(255, 255, 255, 0.7)',
                    fontSize: '0.8vw',
                  } }
                >
                  { currentAccountingBasis }
                </p>
                <p
                  style={ {
                    fontWeight: '600',
                    fontSize: '0.9vw',
                    paddingTop: '1vw',
                  } }
                >
                  BAS Lodgement
                </p>
                <p
                  style={ {
                    color: 'rgb(255, 255, 255, 0.7)',
                    fontSize: '0.8vw',
                  } }
                >
                  { currentBasLodgement }
                </p>
              </>
            ) }
          </div>
        </>
      ) }
      { clientAdding === 'error' && (
        <>
          <div
            style={ {
              textAlign: 'left',
            } }
          >
            <p
              style={ {
                fontSize: '1vw',
                fontWeight: '400',
                marginBottom: '1vw',
              } }
            >
              { errorMessage }
            </p>
          </div>
        </>
      ) }
      <div
        style={ {
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px',
          padding: '0 20px',
          flexDirection: 'column',
        } }
      >
        { (clientAdding === 'init') && (
          <Button
            onClick={ () => {
              if (currentClientName.length === 0) {
                return;
              }
              if (currentAccountingSoftware === 'none' && !currentATORetrieveal) {
                return;
              }
              if (!asAccounts.includes(currentAccountingSoftware)) {
                return;
              }
              setClientAdding('details');
            } }
            variant="outlined"
            style={ {
              borderColor: 'rgb(255, 255, 255, 1)',
              color: 'rgb(255, 255, 255, 0.9)',
              marginLeft: '2vw',
              marginRight: '2vw',
              padding: '0.7vw',
              marginBottom: '1.5vw',
              border: 'none',
              backgroundImage: 'linear-gradient(rgb(255, 255, 255, 0.05), rgb(10, 10, 10, 0.05)),linear-gradient(#f25252, #f25252)',
              borderRadius: '0.5vw',
              textTransform: 'none',
              fontSize: '1vw',
              fontWeight: 500,
              letterSpacing: '0.08vw',
              marginTop: '1vw',
            } }
          >
            Next
          </Button>
        ) }
        { clientAdding === 'details' && (
          <Button
            onClick={ addUser }
            variant="outlined"
            style={ {
              borderColor: 'rgb(255, 255, 255, 1)',
              color: 'rgb(255, 255, 255, 0.9)',
              marginLeft: '2vw',
              marginRight: '2vw',
              padding: '0.7vw',
              marginBottom: '1.5vw',
              border: 'none',
              backgroundImage: 'linear-gradient(rgb(255, 255, 255, 0.05), rgb(10, 10, 10, 0.05)),linear-gradient(#f25252, #f25252)',
              borderRadius: '0.5vw',
              textTransform: 'none',
              fontSize: '1vw',
              fontWeight: 500,
              letterSpacing: '0.08vw',
              marginTop: '1vw',
            } }
          >
            Confirm
          </Button>
        ) }
        { (clientAdding === 'details') && (
          <Button
            onClick={ () => setClientAdding('init') }
            variant="outlined"
            style={ {
              borderColor: 'rgb(255, 255, 255, 1)',
              color: 'rgb(255, 255, 255, 0.8)',
              marginLeft: '2vw',
              marginRight: '2vw',
              padding: '0.7vw',
              marginBottom: '1.5vw',
              border: 'none',
              backgroundColor: 'rgb(255, 255, 255, 0.05)',
              borderRadius: '0.5vw',
              textTransform: 'none',
              fontSize: '1vw',
              fontWeight: 500,
              letterSpacing: '0.08vw',
              marginTop: '0vw',
            } }
          >
            Previous
          </Button>
        ) }
      </div>
    </>
  );
}

function RouteComponent() {
  const queryClient = useQueryClient();
  const {
    clients,
    users,
    availableAccounts,
    subscription,
  } = Route.useLoaderData();
  const maxClients = subscription.limit;

  const auth = useAuth();
  const [
    rawData,
    setRawData,
  ] = useState([]);
  const [
    reactivateSubscriptionShow,
    setReactivateSubscriptionShow,
  ] = useState(false);
  const [
    isSubscriptionActive,
    setIsSubscriptionActive,
  ] = useState(false);
  const navigate = useNavigate();
  // popup
  const [
    addClientsModalId,
    setAddClientsModalId,
  ] = useState<string>();
  const [
    editClientsModalId,
    setEditClientsModalId,
  ] = useState<string>();
  const [
    show,
    setShow,
  ] = useState(false);
  const handleClose = () => {
    setShow(false);
  };
  const handleShow = () => {
    if (!isSubscriptionActive) {
      setReactivateSubscriptionShow(true);
      return;
    }

    if (data.length >= maxClients) {
      setUpgradeRequireShow(true);
      return;
    }

    if (data.length < maxClients) {
      setCurrentABN('');
      setCurrentASClientName('');
      setClientAdding('init');
      setCurrentClientName('');
      if (asAccounts.length === 0) {
        setCurrentAccountingSoftware('none');
      } else {
        setCurrentAccountingSoftware(asAccounts[0]);
      }
      setcurrentAccountingBasis('Cash');
      setcurrentBasLodgement('Quarterly');
      setShow(true);
    }
  };
  const [
    uploadAllowed,
    setUploadAllowed,
  ] = useState(false);
  const [
    filteredData,
    setFilteredData,
  ] = useState([]);
  const [
    statusFilter,
    setStatusFilter,
  ] = useState('all');
  const [
    userData,
    setUserData,
  ] = useState([]);
  const [
    assignedTo,
    setAssignedTo,
  ] = useState([]);
  const [
    asAccounts,
    setAsAccounts,
  ] = useState(availableAccounts);

  // State to manage popup visibility
  const [
    uploadClientShow,
    setUploadClientShow,
  ] = useState(false);
  function agentEmailChangedHandler(agents) {
    const emailsList = agents.map(email => (typeof email === 'object') ? email.label : email);
    const uniqueEmails: string[] = [...new Set(emailsList)];
    setOwnerEmails(uniqueEmails);
  }

  // Handler function to show the popup
  const handleUploadClientShow = () => {
    setUploadClientShow(true);
  };
    // Handler function to hide the popup
  const handleUploadClientClose = () => {
    setUploadClientShow(false);
  };
    // Handler function to handle the template file download
  const handleDownloadTemplate = () => {
    // Add logic to download the template file
    console.log('Download template initiated');
  };
  const [
    uploadedClients,
    setUploadedClients,
  ] = useState([]);
  const [
    currentAccountingSoftware,
    setCurrentAccountingSoftware,
  ] = useState<string>('xero');
  const [
    message,
    setMessage,
  ] = useState<string>('');
  const [
    clientAdding,
    setClientAdding,
  ] = useState<string>('added');
  const [
    sessionExpired,
    setSessionExpired,
  ] = useState(false);
  const [
    upgradeRequiredShow,
    setUpgradeRequireShow,
  ] = useState(false);
  const [
    syncModalShow,
    setSyncModalShow,
  ] = useState(false);
  function fetchAvailableAS() {
    return queryClient.invalidateQueries({
      queryKey: [
        'creator',
        'as-accounts',
      ],
    });
    const availableAccountsFiltered = availableAccounts.filter(item => item.username !== '').map(item => item.acc_type);
    setAsAccounts(availableAccountsFiltered);
    if (availableAccountsFiltered.length === 0) {
      setCurrentAccountingSoftware('none');
    } else {
      setCurrentAccountingSoftware(availableAccountsFiltered[0]);
    }
  }

  function uploadHandler(event) {
    setUploadAllowed(false);
    let count = 2;
    // try{
    Papa.parse(event.target.files[0], {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        try {
          let clients = results.data.map((client) => {
            let adminAccountantEmails = client['Owners'] === '' ? [] : client['Owners'].replace(' ', '').split(',');
            if (!validateAbn(client['ABN'])) {
              throw 'Invalid ABN';
            }
            if (!adminAccountantEmails.includes(auth.user?.profile.preferred_username)) {
              adminAccountantEmails.unshift(auth.user?.profile.preferred_username);
            }
            count++;
            return {
              ABN: client['ABN'],
              clientName: client['Name'],
              financialSWAccount: '',
              financialSWType: 'xero',
              adminAccountantEmails: adminAccountantEmails,
              taxAgentEmail: client['Users'] === '' ? [] : client['Users'].replace(' ', '').split(','),
            };
          });
          setUploadedClients(clients);
          setUploadAllowed(true);
          setMessage(`CSV file validated!`);
        } catch (e) {
          setMessage(`Error at line ${count}: ${e}`);
        }
      },
    });
  }

  // Handler function to handle the file upload
  const handleUploadFile = () => {
    // Add logic to upload the selected file
    console.log('File upload initiated');
  };
  const handleRefresh = () => {
    if (!isLoading) {
      setFilteredData([]);
      setRawData([]);
      setData([]);
      fetchData();
    }
  };
  const [
    data,
    setData,
  ] = useState([]);
  const [
    showPopup,
    setShowPopup,
  ] = useState(false);
  const [
    currentRowId,
    setCurrentRowId,
  ] = useState(null);
    // add new row when create data
  const idRef = useRef();
  const abnRef = useRef();
  const clientNameRef = useRef();
  const alternativeNameRef = useRef();
  const asClientNameRef = useRef();
  const passwordOptionalRef = useRef();
  const accountingSoftwareRef = useRef();
  const AccountingBasis = useRef();
  const basLodgement = useRef();
  const [
    errorMessage,
    setErrorMessage,
  ] = useState('An unexpected error has occured. Please try again later.');
  const [
    errorTitle,
    setErrorTitle,
  ] = useState('Unexpected Error');
    // search textbox
  const [
    searchQuery,
    setSearchQuery,
  ] = useState('');
  useEffect(() => {
    setFilteredData(data.filter(item =>
      item.client_abn.includes(searchQuery)
      || item.client_name.toLowerCase().includes(searchQuery.toLowerCase())
      || item.as_client_name.toLowerCase().includes(searchQuery.toLowerCase())
      || item.financial_sw_type.toLowerCase().includes(searchQuery.toLowerCase()),
    ));
  }, [
    searchQuery,
    data,
  ]);
  // edit
  const [
    currentABN,
    setCurrentABN,
  ] = useState('');
  const [
    currentClientName,
    setCurrentClientName,
  ] = useState('');
  const [
    currentAlternativeName,
    setCurrentAlternativeName,
  ] = useState('');
  const [
    currentASClientName,
    setCurrentASClientName,
  ] = useState('');
  const [
    currentATORetrieveal,
    setCurrentATORetrieveal,
  ] = useState(true);
  const [
    currentAccountingBasis,
    setcurrentAccountingBasis,
  ] = useState('Accrual');
  const [
    currentBasLodgement,
    setcurrentBasLodgement,
  ] = useState('Quarterly');
  const [
    currentStatus,
    setCurrentStatus,
  ] = useState('');
  const [
    currentOwnerEmail,
    setCurrentOwnerEmail,
  ] = useState('');
  const [
    currentUserEmail,
    setCurrentUserEmail,
  ] = useState('');
  const [
    displayMode,
    setDisplayMode,
  ] = useState('active');
  const handleAbnChange = (e) => {
    setAbnValue(e.target.value);
  };
  const handleClientNameChange = (e) => {
    setClientNameValue(e.target.value);
  };
  const handleAsClientNameChange = (e) => {
    setAsClientNameValue(e.target.value);
  };
  const handleAccountingBasisChange = (e) => {
    setAccountingBasisValue(e.target.value);
  };
  const handleBasLodgementChange = (e) => {
    setBasLodgementValue(e.target.value);
  };
  const [
    abnValue,
    setAbnValue,
  ] = useState('');
  const [
    clientNameValue,
    setClientNameValue,
  ] = useState('');
  const [
    asClientNameValue,
    setAsClientNameValue,
  ] = useState('');
  const [
    accountingBasisValue,
    setAccountingBasisValue,
  ] = useState('');
  const [
    basLodgementValue,
    setBasLodgementValue,
  ] = useState('');
  const [
    isLoading,
    setIsLoading,
  ] = useState(false);
  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);
  const itemsPerPage = 8;
  let indexOfLastItem = currentPage * itemsPerPage;
  let indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const [
    currentItems,
    setCurrentitems,
  ] = useState(data.slice(indexOfFirstItem, indexOfLastItem));
  const displayedFirstItem = indexOfFirstItem + 1; // +1 to convert 0-indexed to 1-indexed
  const displayedLastItem = Math.min(indexOfLastItem, filteredData.length); // Use filteredData length here

  const [
    abnValidity,
    setAbnValidity,
  ] = useState(true);
  const [
    reloadButtonDisabled,
    setReloadButtonDisabled,
  ] = useState(false);
    // Page change handlers
  const nextPage = () => {
    if (currentPage < Math.ceil(data.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  useEffect(() => {
    // Calculate the items for the current page from filtered data
    let indexOfLastItem = currentPage * itemsPerPage;
    let indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentitems(filteredData.slice(indexOfFirstItem, indexOfLastItem));
  }, [
    filteredData,
    currentPage,
  ]);
  useEffect(() => {
    if (currentRowId !== null) {
      const currentItem = data.find(item => item.id === currentRowId);
      if (currentItem) {
        setCurrentABN(currentItem.client_abn);
        setCurrentClientName(currentItem.client_name);
        setCurrentAlternativeName(currentItem.alt_client_name);
        setCurrentASClientName(currentItem.as_client_name);
        setCurrentAccountingSoftware(currentItem.financial_sw_type);
        setcurrentAccountingBasis(currentItem.accounting_basis);
        setcurrentBasLodgement(currentItem.bas_lodgement);
        setCurrentStatus(currentItem.status);
        setCurrentOwnerEmail(currentItem.owners);
        setCurrentUserEmail(currentItem.users);
      }
    }
  }, [
    currentRowId,
    data,
  ]);
  // new
  const [
    ownerEmails,
    setOwnerEmails,
  ] = useState<string[]>([]);
  const [
    userEmails,
    setUserEmails,
  ] = useState([]);
    // useEffect(() => {
    //   if (data.length > 0) {
    //     setOwnerEmails(data[0].owners);
    //     setUserEmails(data[0].users);
    //   }
    // }, [data]);
  function transformData(prevState, users) {
    const newState = [...prevState];
    newState.forEach((client) => {
      let owners = client.owners;
      console.log(users.filter(item => owners.includes(item.email)));
      client.owners = users.filter(item => owners.includes(item.email));
    });
    return newState;
  }

  function retrieveUserData() {
    fetch(`${window.origin}/api/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
    }).then((response) => {
      if (response.status === 403) {
        setSessionExpired(true);
        throw new Error('Session expired');
      } else {
        return response.json();
      }
    }).then((users) => {
      setUserData(users.filter(item => item.email !== auth.user?.profile.email));
    }).catch((error) => {
      console.error(error);
    });
  }
  useEffect(() => {
    const successData = rawData.filter(item =>
      item.status.includes('done'),
    );
    const failData = rawData.filter(item =>
      item.status.includes('fail'),
    );
    if (displayMode === 'active') {
      setData(rawData.filter(item =>
        item.status.includes('done'),
      ));
    } else {
      setData(rawData.filter(item =>
        item.status.includes('fail'),
      ));
    }
  }, [
    displayMode,
    rawData,
  ]);
  const handleABNChange = async () => {
    const abn = abnRef.current.value;
    if (abn) {
      const isValid = await validateAbn(abn);
      setAbnValidity(isValid);
    } else {
      setAbnValidity(null);
    }
  };
  const fetchData = () => {
    fetch(`${window.origin}/api/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
    }).then((response) => {
      if (response.status === 403) {
        setSessionExpired(true);
        throw new Error('Session expired');
      } else {
        return response.json();
      }
    }).then((users) => {
      let tempUserData = users;
      setUserData(tempUserData);
      setReloadButtonDisabled(true);
      setIsLoading(true);
      fetch(`${window.origin}/api/creator/clients`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            setSessionExpired(true);
            throw new Error('Session expired');
          } else {
            return response.json();
          }
        })
        .then((clients) => {
          let transformedClients = transformData(clients, tempUserData);
          setRawData(transformedClients);
          setIsLoading(false);
          setReloadButtonDisabled(false);
        })
        .catch((error) => {
          console.error('Error fetching clients:', error);
          setReloadButtonDisabled(false);
        });
    }).catch((error) => {
      console.error(error);
    });
  };
  async function addUser() {
    handleBackdropOpen();
    console.debug(currentAccountingSoftware);
    // setCurrentABN(validateAbn(abnRef?.current?.value) ? abnRef?.current?.value : 'NOT PROVIDED')
    // setCurrentASClientName(asClientNameRef?.current?.value !== '' ? asClientNameRef?.current?.value : 'NOT PROVIDED')
    console.debug(validateAbn(currentABN) || 'NOT PROVIDED');
    try {
      let response = await fetch(`${window.origin}/api/creator/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user?.access_token}`,
        },
        body: JSON.stringify({
          clients: [
            {
              client_abn: validateAbn(currentABN) || 'NOT PROVIDED',
              client_name: currentClientName || '',
              alt_client_name: '',
              as_client_name: currentASClientName !== '' ? currentASClientName : 'NOT PROVIDED',
              financial_sw_type: currentAccountingSoftware || '',
              ato_data: currentATORetrieveal || false,
              accounting_basis: currentAccountingBasis || '',
              bas_lodgement: currentBasLodgement || '',
              owners: [auth.user?.profile.preferred_username],
              password: passwordOptionalRef?.current?.value || '',
              users: userEmails,
            },
          ],
        }),
      });
      console.log(response.status);
      if (response.status !== 200) {
        setClientAdding('error');
        let json_resp = await response.json();
        if (json_resp.error === 'Client already exists.') {
          // setSnackBarMessage('Client already exist');
          // setSnackBarShow(true);
          handleClose();
        } else {
          // setSnackBarMessage('Unknown error occurred. Please try again later');
          // setSnackBarShow(true);
          handleClose();
        }
      } else {
        // setSnackBarMessage('Client successfully added');
        // setSnackBarShow(true);
        handleClose();
        handleRefresh();
      }

      if (response.status === 403) {
        setSessionExpired(true);
        throw new Error('Session expired');
      }
    } catch (e) {
      // setSnackBarMessage('Unknown error occurred. Please try again later');
      // setSnackBarShow(true);
      handleClose();
    }
    handleBackdropClose();
  }

  const [
    deleteID,
    setDeleteID,
  ] = useState(null);
  const [
    deleteDialogShow,
    setDeleteDialogShow,
  ] = useState(false);
  const handleDeleteDialogClickOpen = () => {
    setDeleteDialogShow(true);
  };
  function deleteClient(idToDelete) {
    console.log('Attempting to delete client with ID:', idToDelete);
    setShowPopup(false);
    fetch(`${window.origin}/api/creator/clients?id=${idToDelete}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
    })
      .then((response) => {
        if (response.status === 403) {
          auth.signoutSilent();
          setSessionExpired(true);
        } else if (response.ok) {
          // Update your client data state to remove the deleted client
          setData(prevData => prevData.filter(item => item.id !== idToDelete));
          handleClose(); // Close the modal after successful deletion
          setDeleteDialogShow(false);
          handleRefresh();
        } else {
          // Handle other response statuses or errors
          console.error('Error deleting client. Status:', response.status);
        }
      })
      .catch((error) => {
        console.error('Error deleting client:', error);
        handleRefresh();
      });
  }

  function editClientHandler(client) {
    // Check if client object has an id
    if (!client.id) {
      console.error('Client ID is missing');
      return;
    }

    setIsLoading(true);
    fetch(`${window.origin}/api/creator/clients`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${auth.user?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(client),
    })
      .then((response) => {
        if (response.status === 403) {
          setSessionExpired(true);
          throw new Error('Session expired');
        }
        return response.json();
      })
      .then((updatedClient) => {
        // setSnackBarMessage('Client details updated');
        // setSnackBarShow(true);
        fetchData();
        console.log(updatedClient);
      })
      .catch((error) => {
        console.error('Error editing client:', error);
      });
  }

  useEffect(() => {
    let interval = setInterval(() => {
      fetchData();
    }, 120000);
    return () => {
      clearInterval(interval);
      console.log('Interval cleared.');
    };
  }, []);
  useEffect(() => {
    fetch(`${window.origin}/api/creator/subscription/active`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
    }).then((response) => {
      return response.json();
    }).then((plan) => {
      setIsSubscriptionActive(plan[0].subscription_status === 'active');
    });
  }, []);
  return (
    <div
      style={ {
        textAlign: 'center',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
      } }
    >
      <MessageModalModified
        show={ reactivateSubscriptionShow }
        onClose={ () => { setReactivateSubscriptionShow(false); } }
        onClick={ () => {
          navigate('/mydigitalworkers/gary/settings', {
            state: {
              tab: 'SubscriptionSettings',
            },
          });
        } }
        message="Subscription Canceled"
        description="Your subscription has been canceled. Please re-activate your subscription to continue."
        buttonText="Manage Subscription"
      />
      <MessageModalModified
        show={ upgradeRequiredShow }
        onClose={ () => { setUpgradeRequireShow(false); } }
        onClick={ () => {
          navigate('/mydigitalworkers/gary/settings', {
            state: {
              tab: 'SubscriptionSettings',
            },
          });
        } }
        message="Upgrade Required"
        description="You have reached the maximum number of clients for the current plan. Please upgrade to add more clients."
        buttonText="Upgrade"
      />
      <div
        style={ {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '81vh',
        } }
      >
        <div
          style={ {} }
        >
          { /* top button */ }
          <div
            style={ {
              margin: '1.3vw 1vw',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              lineHeight: '0',
            } }
          >
            <div
              style={ {
                flex: 1,
                paddingLeft: '1vw',
              } }
            >
              <div
                style={ {
                  textAlign: 'left',
                  marginBottom: '0.5vw',
                  width: '15vw',
                } }
              >
                <div
                  style={ {
                    backgroundColor: 'rgb(255, 255, 255, 0.08)',
                    borderRadius: '50px',
                    padding: '0.2vw',
                  } }
                >
                  <ThemeProvider
                    theme={ darkTheme }
                  >

                    <IconButton
                      aria-label="delete"
                      style={ {
                        backgroundColor: 'rgb(255, 255, 255, 0.15)',
                        padding: '0.5vw',
                        fontSize: '0.5vw',
                        color: 'rgb(255, 255, 255, 0.7)',
                        cursor: 'none',
                      } }
                    >
                      <SearchIcon
                        id="client-search-bar"
                        style={ {
                          fontSize: '1vw',
                        } }
                      />
                    </IconButton>
                  </ThemeProvider>
                  <Form.Control
                    style={ {
                      display: 'inline',
                      backgroundColor: 'rgba(255,255,255,0)',
                      padding: 0,
                      paddingLeft: '0.8vw',
                      paddingRight: '0.8vw',
                      border: '1px solid rgb(255, 255, 255, 0)',
                      borderRadius: '6px',
                      fontSize: '0.9vw',
                      maxWidth: '80%',
                      marginLeft: '0',
                      fontWeight: '200',
                    } }
                    className="shadow-none"
                    type="text"
                    placeholder="Search"
                    value={ searchQuery }
                    onChange={ e => setSearchQuery(e.target.value) }
                  />
                </div>
              </div>
            </div>
            <div
              style={ {
                flex: 1,
                textAlign: 'right',
                paddingLeft: '1vw',
                paddingRight: '1vw',
              } }
            >
              <span
                style={ {
                  flex: 1,
                  fontSize: '1.8vh',
                  textAlign: 'center',
                  flexDirection: 'row',
                } }
              >
                <div
                  style={ {
                    display: 'inline-block',
                    backgroundColor: 'rgb(255, 255, 255, 0.1)',
                    borderRadius: '2vw',
                    padding: '0.2vw',
                  } }
                >
                  <TransparentButton
                    style={ {
                      padding: '0.3vw 0.75vw',
                    } }
                    onClick={ () => {
                      setDisplayMode('active');
                    } }
                    selected={ displayMode === 'active' }
                  >
                    <VerifiedIcon
                      style={ {
                        fontSize: '1vw',
                      } }
                    />
                  </TransparentButton>
                  <TransparentButton
                    style={ {
                      padding: '0.3vw 0.75vw',
                    } }
                    onClick={ () => {
                      setDisplayMode('fail');
                    } }
                    selected={ displayMode === 'fail' }
                  >
                    <NewReleasesIcon
                      style={ {
                        fontSize: '1vw',
                      } }
                    />
                  </TransparentButton>
                </div>
              </span>
              { auth.user?.profile.groups.includes('gary_admin') && (
                <IconButton
                  id="add-new-client"
                  variant="text"
                  style={ {
                    fontSize: '0.9vw',
                    cursor: 'pointer',
                    padding: '0.75vw',
                    minWidth: 0,
                    borderRadius: '2vw',
                    backgroundColor: 'rgb(255, 255, 255, 0.1)',
                    marginRight: '0.5vw',
                    marginLeft: '1vw',
                  } }
                  onClick={
                    addClientsModalId(
                      modals.open({
                        children: AddClientsModal(
                          clientAdding,
                          currentABN,
                        ),
                        withCloseButton: false,
                        closeOnClickOutside: true,
                        closeOnEscape: true,
                        centered: true,
                        bg: 'var(--mantine-color-primary-9)',
                        right: 0,
                        onClose: handleClose,

                      }),
                    )
                  }
                />
              )}
            </div>
          </div>
          <SyncClientsModal
            show={ syncModalShow }
            asAccounts={ asAccounts }
            onHide={ () => {
              setSyncModalShow(false);
            } }
          />
          { /* popup for uploading client setup details */ }
          <Offcanvas
            show={ uploadClientShow }
            onHide={ handleUploadClientClose }
            placement="end"
            style={ {
              'backgroundColor': 'rgba(32, 30, 34, 0.7)',
              '-webkit-backdrop-filter': 'blur(10px)',
              'backdropFilter': 'blur(50px)',
              'border': '1px solid rgba(255, 255, 255, 0.2)',
              'paddingLeft': '1vw',
              'paddingRight': '1vw',
              'paddingTop': '1vw',
              'color': 'white',
              'display': 'flex',
              'flexDirection': 'column',
              'margin': '1vw',
              'borderRadius': '1vw',
              'width': '23vw',
            } }
          >
            <div
              style={ {
                flex: 1,
              } }
            >
              <Offcanvas.Header>
                <Offcanvas.Title
                  style={ {
                    fontSize: '2vw',
                  } }
                >
                  Upload Bulk Clients
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body
                style={ {
                  paddingTop: '1vw',
                  height: '100%',
                } }
              >
                <Form.Group
                  controlId="formFileLg"
                  className="mb-3"
                >
                  <p
                    style={ {
                      fontSize: '1.6vw',
                      fontWeight: '500',
                    } }
                  >
                    Instructions
                  </p>
                  <p
                    style={ {
                      marginBottom: 0,
                      fontSize: '1vw',
                    } }
                  >
                    1. Fill in the CSV template
                  </p>
                  <div
                    style={ {
                      padding: '1vw 0',
                    } }
                  >
                    <a
                      className="btn btn-primary"
                      style={ {
                        fontSize: '1vw',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        borderRadius: '2vw',
                        padding: '1vw 2.5vw',
                        margin: '0.5vw 0',
                      } }
                    >
                      Download template
                    </a>
                  </div>
                  <Form.Label
                    style={ {
                      marginBottom: '2vw',
                      marginTop: '2vw',
                      fontSize: '1vw',
                    } }
                  >
                    2. Upload the CSV template
                  </Form.Label>
                  <div
                    className="form-group files"
                    style={ {
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5vw',
                      padding: '2vw',
                      width: '100%',
                      height: '15vw',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    } }
                  >
                    <i
                      className="fa-solid fa-upload"
                      style={ {
                        color: '#ffffff',
                        fontSize: '1.6vw',
                      } }
                    >
                    </i>
                    <input
                      style={ {
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        opacity: 0,
                        cursor: 'pointer',
                        fontSize: '1.3vw',
                      } }
                      type="file"
                      className="form-control"
                      multiple
                      accept=".csv"
                    />
                    <div
                      style={ {
                        zIndex: 1,
                        fontSize: '1vw',
                      } }
                    >
                      Choose a file.
                    </div>
                  </div>
                </Form.Group>
              </Offcanvas.Body>
            </div>

            <div
              style={ {
                textAlign: 'right',
                paddingBottom: '1.5vw',
              } }
            >
              <span
                style={ {
                  fontSize: '1.3vw',
                  color: '#ee7170',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '1.2vw',
                  paddingBottom: '0vw',
                } }
                onClick={ handleUploadClientClose }
              >
                Close
              </span>
              <button
                type="button"
                style={ {
                  // color: uploadAllowed ? 'rgb(255, 255, 255, 0.9)' : 'rgb(255, 255, 255, 0.6)',
                  fontSize: '1.3vw',
                  color: '#ee7170',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '1.2vw',
                  paddingBottom: '0vw',
                  backgroundColor: 'transparent',
                  border: 'transparent',
                } }
                onClick={ handleUploadFile }
                disabled={ !uploadAllowed }
              >
                Upload
              </button>
            </div>
          </Offcanvas>

          <ThemeProvider
            theme={ darkTheme }
          >

            { /* table */ }
            <div
              style={ {
                backdropFilter: 'blur(50px)',
                lineHeight: '2vw',
                marginLeft: '2vw',
                marginRight: '2vw',
                border: '1px solid rgb(255, 255, 255, 0.15)',
                borderRadius: '1.5vw',
                textAlign: 'left',
              } }
            >
              <List
                className="table-hover table-dark table"
                style={ {
                  color: 'rgb(255, 255, 255, 0.9)',
                  borderRadius: '1vw',
                  fontSize: '0.75vw',
                  backgroundColor: 'rgb(255, 255, 255, 0.0)',
                  margin: 0,
                  padding: 0,
                } }
              >
                <ListSubheader
                  style={ {
                    fontWeight: 600,
                    fontSize: '0.7vw',
                    lineHeight: '3vw',
                    backgroundColor: 'rgb(255, 255, 255, 0.06)',
                    color: 'rgb(255, 255, 255, 0.85)',
                    borderTopLeftRadius: '1.5vw',
                    borderTopRightRadius: '1.5vw',
                    padding: '0 2vw',
                    display: 'flex',
                    flexDirection: 'row',
                  } }
                >
                  { /* <div style={{display: 'flex', fontWeight: '600'}}> */ }
                  <div
                    scope="col"
                    style={ {
                      padding: '0.1vw 0',
                      flex: 3,
                    } }
                  >
                    ABN
                  </div>
                  <div
                    scope="col"
                    style={ {
                      padding: '0.1vw 0',
                      flex: 6,
                    } }
                  >
                    CLIENT NAME
                  </div>
                  { /* <th scope="col" style ={{padding:'0.5vw 1.5vw', width: '30%'}}>AS CLIENT NAME</th> */ }
                  <div
                    scope="col"
                    style={ {
                      padding: '0.1vw 0',
                      flex: 3,
                    } }
                  >
                    ATO DATA
                  </div>
                  <div
                    scope="col"
                    style={ {
                      padding: '0.1vw 0',
                      flex: 3,
                    } }
                  >
                    ACCOUNTING SOFTWARE
                  </div>
                  { /* <th scope="col" style ={{padding:'0.5vw 1.5vw'}}>Accounting Basis</th>
                                      <th scope="col" style ={{padding:'0.5vw 1.5vw'}}>BAS Lodgement</th> */ }
                  <div
                    scope="col"
                    style={ {
                      padding: '0.1vw 0',
                      flex: 1,
                    } }
                  >
                    STATUS
                  </div>

                  { /* </div> */ }
                </ListSubheader>
                <div
                  style={ {
                    fontSize: '0.65vw',
                    color: 'rgb(255, 255, 255, 0.75)',
                  } }
                >
                  { currentItems.map(item => (
                    <ListItemButton
                      id={ `client-abn-${item.client_abn}` }
                      key={ item.id }
                      style={ {
                        cursor: 'pointer',
                        lineHeight: '2vw',
                        display: 'flex',
                        flexDirection: 'row',
                        borderTop: '1px solid rgb(255, 255, 255, 0.05)',
                        fontSize: '0.8vw',
                        padding: '0.5vw 2vw',
                      } }
                      onClick={ () => {
                        setDeleteID(item.id);
                        setCurrentABN(item.client_abn);
                        setCurrentClientName(item.client_name);
                        setCurrentAlternativeName(item.alt_client_name);
                        setCurrentASClientName(item.as_client_name);
                        setAsClientNameValue(item.as_client_name);
                        setCurrentAccountingSoftware(item.financial_sw_type);
                        setcurrentAccountingBasis(item.accounting_basis);
                        setcurrentBasLodgement(item.bas_lodgement);
                        setCurrentStatus(item.client_status);
                        setOwnerEmails(item.owners);
                        console.log(item);
                        setCurrentATORetrieveal(item.ato_data);
                        setShowPopup(true);
                      } }
                    >
                      <div
                        scope="col"
                        style={ {
                          padding: '0.1vw 0',
                          flex: 3,
                          fontFamily: '\'Consolas\', Courier, monospace',
                          letterSpacing: formatAbn(item.client_abn) === 'NOT PROVIDED' ? '0.15vw' : '0.05vw',
                        } }
                      >
                        { formatAbn(item.client_abn) }
                      </div>
                      <div
                        scope="col"
                        style={ {
                          padding: '0.1vw 0',
                          flex: 6,
                        } }
                      >
                        { item.client_name }
                      </div>
                      <div
                        scope="col"
                        style={ {
                          padding: '0.1vw 0',
                          flex: 3,
                        } }
                      >
                        { item.ato_data ? 'YES' : 'NO' }
                      </div>
                      <div
                        scope="col"
                        style={ {
                          padding: '0.1vw 0',
                          flex: 3,
                        } }
                      >
                        { item.financial_sw_type.toUpperCase() }
                      </div>
                      <div
                        scope="col"
                        style={ {
                          padding: '0.1vw 0',
                          flex: 1,
                        } }
                      >
                        <OverlayTrigger
                          overlay={ (
                            <Tooltip
                              id="tooltip-disabled"
                            >
                              { item.status_detail ? item.status_detail : 'Unknown Message' }
                            </Tooltip>
                          ) }
                        >
                          <span
                            className="d-inline-block"
                          >
                            <span>
                              { item.status === 'done' ? 'Active' : (item.status === 'ctlreq' || item.status === 'processing' ? 'Pending' : 'Fail') }
                            </span>
                          </span>
                        </OverlayTrigger>
                      </div>
                    </ListItemButton>
                  )) }
                  { currentItems.length === 0 && !isLoading
                  && (
                    <div
                      style={ {
                        display: 'flex',
                      } }
                    >
                      <div
                        style={ {
                          flex: 1,
                        } }
                      >
                        <p
                          style={ {
                            textAlign: 'center',
                            marginTop: '30px',
                            marginBottom: '30px',
                          } }
                        >
                          No client found
                        </p>
                      </div>
                    </div>
                  ) }
                  { currentItems.length === 0 && isLoading
                  && (
                    <div
                      style={ {
                        display: 'flex',
                      } }
                    >
                      <div
                        style={ {
                          flex: 1,
                        } }
                      >
                        <p
                          style={ {
                            textAlign: 'center',
                            marginTop: '30px',
                            marginBottom: '30px',
                          } }
                        >
                          <CircularProgress
                            color="inherit"
                          />
                        </p>
                      </div>
                    </div>
                  ) }
                </div>
              </List>
            </div>
          </ThemeProvider>

          <div
            style={ {
              marginTop: '1vw',
              marginLeft: '2vw',
              marginRight: '2vw',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'left',
            } }
          >
            <div
              style={ {
                flex: 1,
                marginLeft: '0.5vw',
              } }
            >
              <p
                style={ {
                  color: 'rgb(255, 255, 255, 0.5)',
                  fontSize: '0.8vw',
                  lineHeight: 'normal',
                } }
              >
                { `Showing ${displayedFirstItem} to ${displayedLastItem} of ${filteredData.length} entries` }
              </p>
            </div>
            <div
              style={ {
                display: 'flex',
                alignItems: 'center',
              } }
            >
              <button
                onClick={ prevPage }
                disabled={ currentPage === 1 }
                style={ {
                  width: '2.5vw',
                  height: '2.5vw',
                  borderRadius: '0.5vw',
                  marginRight: '6px',
                  color: currentPage === 1 ? 'gray' : 'white',
                  backgroundColor: 'rgb(255, 255, 255, 0.02)',
                  border: '0.5px solid rgb(255, 255, 255, 0.3)',
                  // color: 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9vw',
                } }
              >
                <span
                  dangerouslySetInnerHTML={ {
                    __html: '&larr;',
                  } }
                />
              </button>

              <button
                onClick={ nextPage }
                disabled={ currentPage === Math.ceil(data.length / itemsPerPage) }
                style={ {
                  width: '2.5vw',
                  height: '2.5vw',
                  borderRadius: '0.5vw',
                  marginLeft: '6px',
                  color: 'white',
                  backgroundColor: 'rgb(255, 255, 255, 0.02)',
                  border: '0.5px solid rgb(255, 255, 255, 0.3)',
                  cursor: currentPage === Math.ceil(data.length / itemsPerPage) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9vw',
                } }
              >
                <span
                  dangerouslySetInnerHTML={ {
                    __html: '&rarr;',
                  } }
                />
              </button>
            </div>
          </div>

          { /* offcanvas for */ }
        </div>
      </div>

      <ThemeProvider
        theme={ darkTheme }
      >
        <Dialog
          open={ deleteDialogShow }
          onClose={ () => setDeleteDialogShow(false) }
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"

        >
          <DialogTitle
            id="alert-dialog-title"
          >
            Client Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="alert-dialog-description"
            >
              Please confirm that you want to delete
              { ' ' }
              { currentClientName }
              { ' ' }
              from client list.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={ () => setDeleteDialogShow(false) }
            >
              Cancel
            </Button>
            <Button
              onClick={ () => deleteClient(deleteID) }
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </div>
  );
};
